/**
 * fibulaRecipes.js — the page types of the free fibula flap report.
 *
 * In page mode (ADR 0015) a template needs no complete recipe set before it is
 * usable: every page type here is one the operator can pick today, and the rest
 * of the report is still made by hand and merged, as it is now. So this file
 * grows one page at a time, each checked against the delivered report.
 *
 * ── What each page was checked against ─────────────────────────────────────
 *
 * The delivered 23-page V1.9 report at
 * /Users/deepakrajk/Documents/Temp/Models and Case report of FFF case/ (image
 * only — read visually, page by page) and the team's V1.9.2 template, whose
 * variant names these pages claim in pageTypes.js. Captions are the delivered
 * report's own wording and case.
 *
 *   · Agreement and both Terms pages: compared WORD FOR WORD against delivered
 *     pp. 1, 22 and 23 — identical to the orthognathic boilerplate, down to the
 *     source's own "of the The Online Surgical Planning". Shared, not copied.
 *   · The six-view pages: composition and captions from delivered pp. 3, 6, 8,
 *     15 and 18.
 *
 * ── Laterality is DERIVED, never written ──────────────────────────────────
 *
 * The delivered report says "Left fibula was used in the reconstruction" on
 * five pages; the template's own copy of that sentence says "Right". A literal
 * here would be a wrong statement about the patient on every case of the other
 * side. The side comes from the fibula and leg files' names (buildLibrary's
 * `side`); files that disagree are reported, and an unknown side leaves the
 * sentence out with a note saying so — never a guess.
 *
 * These are suggested pages — one-click presets. Any other page of the report
 * is made from the generic page types (genericPages.js): six views or a single
 * view of the meshes the operator picks, the CT data page, the text pages.
 */
import {
  AGREEMENT_BODY, TERMS_BODY_1, TERMS_BODY_2, AGREEMENT_STYLES, TERMS_STYLES,
} from './boilerplate.js'

/** Roles whose filename can say which leg the fibula came from. */
const LATERAL_ROLES = new Set(['fibula', 'leg', 'fibula_segments', 'fibula_guide'])

/**
 * Which leg the fibula came from, read from the files that can say.
 *
 * @returns {{side: 'left'|'right'|null, conflict: boolean, from: string[]}}
 *   `from` names the files that stated a side, so a conflict can be shown.
 */
export function reconstructionSide(library) {
  const stated = (library ?? []).filter((m) => LATERAL_ROLES.has(m.role) && m.side)
  const sides = new Set(stated.map((m) => m.side))
  const from = stated.map((m) => `${m.name} (${m.side})`)
  if (sides.size > 1) return { side: null, conflict: true, from }
  return { side: sides.size ? [...sides][0] : null, conflict: false, from }
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * The sentence naming the fibula used, or '' — plus a note when it is left
 * out, because the delivered report always carries it and its absence must be
 * a stated choice, not something the operator discovers in the PDF.
 */
function lateralitySentence(ctx) {
  // The CASE's files, not this page's. Which leg the fibula came from is read
  // off the leg and fibula meshes, and the two pages that state this sentence —
  // the reconstructed mandible and the post-plan skull — show neither of them.
  // Reading the page's own files made the sentence vanish on the ordinary
  // workflow, and state a side on a case whose files disagree.
  const s = reconstructionSide(ctx.caseLibrary ?? ctx.library)
  if (s.side) return `${cap(s.side)} fibula was used in the reconstruction`
  ctx.notes.push({
    page: ctx.recipe.title,
    kind: 'incomplete',
    reason: s.conflict
      ? `built WITHOUT the sentence naming which fibula was used — the files disagree: ${s.from.join(', ')}. `
        + 'Check which leg, then add the sentence as the bottom caption.'
      : 'built WITHOUT the sentence naming which fibula was used — no filename says which leg. '
        + 'Add it as the bottom caption, e.g. "Left fibula was used in the reconstruction".',
    missing: ['laterality'],
  })
  return ''
}

/** The skip note, in draft.js's own words: ambiguous is not the same as absent. */
function skip(ctx, specs) {
  const why = (spec) => {
    const n = ctx.candidates(...spec).length
    return n > 1
      ? `${ctx.missingLabel(spec)} is ambiguous (${n} candidates — choose one)`
      : `${ctx.missingLabel(spec)} is not in this case`
  }
  ctx.notes.push({
    page: ctx.recipe.title,
    kind: 'skipped',
    reason: `not built — ${specs.map(why).join('; ')}`,
    missing: specs.map(ctx.missingLabel),
  })
}

/** Resolve every spec; skip the page, saying why, if any is missing. */
function need(ctx, specs) {
  const got = specs.map((spec) => ctx.resolve(...spec))
  const missing = specs.filter((_, i) => !got[i])
  if (missing.length) { skip(ctx, missing); return null }
  return got
}

export const FIBULA_RECIPES = [
  {
    id: 'agreement', title: 'Agreement', kind: 'text',
    needs: [], build: () => AGREEMENT_BODY, styles: AGREEMENT_STYLES,
  },
  {
    // Delivered p3. The FULL native skull, native mandible included — which is
    // why the case folder's other skull (the cropped one, for the post-plan
    // page) is a rival here and the operator picks per page.
    id: 'native-skull', title: 'Native skull', kind: 'scene',
    sceneName: 'Native Skull', topCaption: 'Native skull', bottomCaption: 'Preoperative images',
    needs: [['skull', 'native']],
    // Shared with the post-plan skull, so the two are framed at one scale and
    // can be compared.
    fitGroup: 'plan',
  },
  {
    // Delivered p6: the mandible with the planned resection in red. The
    // remaining mandible and the resected piece are two files on the reference
    // case. The team also exports them as ONE file, the piece painted into the
    // mandible: "Resected mandible DS TR.OBJ" on O3D-2026-09-15-0002. That file
    // fills the mandible slot only, and requiring both slots left the page unbuilt
    // (team, 19 Oct 2026: "Although the relevant file is uploaded and assigned to
    // the section, the page is not being rendered"). So the mandible builds the
    // page, and a separate piece is drawn when there is one.
    //
    // No note when there is none. One was added on 19 Oct and removed on 20 Oct
    // (Deepak: "REMOVE"): nobody asked for it, and it fired on every one-file case.
    id: 'resection-area', title: 'Resection area', kind: 'custom',
    compose(ctx) {
      // Both asked before either decides, so the page offers the segment slot even
      // while it has no mandible yet.
      const mandible = ctx.resolve('mandible', 'postplan')
      const segment = ctx.resolve('resection', 'postplan')
      if (!mandible) { skip(ctx, [['mandible', 'postplan']]); return }
      if (!segment && ctx.wasAmbiguous('resection', 'postplan')) {
        const label = ctx.missingLabel(['resection', 'postplan'])
        ctx.notes.push({
          page: ctx.recipe.title,
          kind: 'incomplete',
          reason: `built WITHOUT ${label} — more than one candidate; choose which mesh to use and re-draft`,
          missing: [label],
        })
      }
      const parts = [mandible, segment].filter(Boolean).map(ctx.partFor)
      const sceneId = ctx.addScene('Resection area', parts, undefined, ctx.impliedFrame(parts))
      ctx.pushSixViewPage(sceneId, { tc: 'Resection area', bc: '' }, ctx.recipe.id)
    },
  },
  {
    // Delivered p8: the mandible with EVERY mandible resection guide on it — the
    // case has an anterior and a posterior guide, and the page shows both. So the
    // guides come from candidates(), not resolve(): resolve is for a slot where
    // one must be chosen, and choosing one here would drop the other.
    //
    // The delivered caption adds "- Fixation hole diameter = 2mm". That number
    // is typed per case and carried by no file, so it is not printed here; the
    // operator adds it to the caption. A page must never print "= mm".
    id: 'mandible-resection-guides', title: 'Mandible resection guides', kind: 'custom',
    compose(ctx) {
      const got = need(ctx, [['mandible', 'postplan']])
      if (!got) return
      const guides = ctx.candidates('mandible_guide', 'output')
      if (!guides.length) { skip(ctx, [['mandible_guide', 'output']]); return }
      const segment = ctx.resolve('resection', 'postplan')
      if (!segment && ctx.wasAmbiguous('resection', 'postplan')) {
        ctx.notes.push({
          page: ctx.recipe.title,
          kind: 'incomplete',
          reason: 'built WITHOUT the resected segment — more than one candidate; choose which mesh to use and re-draft',
          missing: [ctx.missingLabel(['resection', 'postplan'])],
        })
      }
      const parts = [got[0], segment, ...guides].filter(Boolean).map(ctx.partFor)
      const sceneId = ctx.addScene('Mandible resection guides', parts, undefined, ctx.impliedFrame(parts))
      const tc = guides.length === 1 ? 'Mandible resection guide' : 'Mandible resection guides'
      ctx.pushSixViewPage(sceneId, { tc, bc: '' }, ctx.recipe.id)
    },
  },
  {
    // Delivered p15 / template "Page 16 Reconstructed Mandible Color". The
    // segments' colours come from the operator's polygroup declaration (Part
    // types), because no file carries them.
    id: 'reconstructed-mandible', title: 'Reconstructed mandible', kind: 'custom',
    compose(ctx) {
      const got = need(ctx, [['reconstructed_mandible', 'postplan']])
      if (!got) return
      const parts = got.map(ctx.partFor)
      const sceneId = ctx.addScene('Reconstructed mandible', parts, undefined, ctx.impliedFrame(parts))
      ctx.pushSixViewPage(sceneId, { tc: 'Reconstructed mandible', bc: lateralitySentence(ctx) }, ctx.recipe.id)
    },
  },
  {
    // Delivered p18: the skull WITHOUT its native mandible, with the
    // reconstructed mandible in its place — so on the reference case this is the
    // cropped skull, chosen on this page, while the native-skull page uses the
    // full one. Framed with the native skull ('plan') so the two compare.
    //
    // The team also exports the whole page as ONE file, the reconstructed
    // mandible already in the skull: "Post plan skull.OBJ" on O3D-2026-09-15-0002,
    // which is a POST-PLAN skull and fills neither slot of the pair (team, 19 Oct
    // 2026: the page was not rendered). Such a file is the page. A native skull
    // alone is still not: it would print the pre-operative skull as the plan.
    //
    // The pair is drawn whenever it can be. A pair slot with two candidates is
    // the operator's question, and answering it draws the pair, so it is asked
    // rather than stepped round. Only when the pair cannot be drawn whatever the
    // answer, because a slot is ABSENT, is one post-plan skull the page.
    id: 'post-plan-skull', title: 'Post plan skull', kind: 'custom',
    compose(ctx) {
      const pair = [['skull', 'native'], ['reconstructed_mandible', 'postplan']]
      const got = pair.map((spec) => ctx.resolve(...spec))
      const missing = pair.filter((_, i) => !got[i])
      const pairPossible = missing.every((spec) => ctx.wasAmbiguous(...spec))
      const whole = pairPossible ? null : ctx.resolve('skull', 'postplan')
      if (missing.length && !whole) {
        // Two post-plan skulls are the reason when the pair is out of reach;
        // naming the pair's files would send the operator after the wrong ones.
        skip(ctx, !pairPossible && ctx.wasAmbiguous('skull', 'postplan') ? [['skull', 'postplan']] : missing)
        return
      }
      const parts = (missing.length ? [whole] : got).map(ctx.partFor)
      const sceneId = ctx.addScene('Post plan skull', parts, 'plan', ctx.impliedFrame(parts))
      ctx.pushSixViewPage(sceneId, { tc: 'Post plan skull', bc: lateralitySentence(ctx) }, ctx.recipe.id)
    },
  },
  {
    id: 'terms-1', title: 'Terms and Conditions (1 of 2)', kind: 'text',
    needs: [], build: () => TERMS_BODY_1, styles: TERMS_STYLES,
  },
  {
    id: 'terms-2', title: 'Terms and Conditions (2 of 2)', kind: 'text',
    needs: [], build: () => TERMS_BODY_2, styles: TERMS_STYLES,
  },
]
